import React, {useMemo, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchControlDepartments} from '@/entities/attendance/api';
import {
  bulkDetachPersonnel,
  bulkSetAccessUntil,
  bulkSetPersonnelDepartments,
  exportPersonnel,
  importPersonnel,
  type ImportFile,
} from '@/entities/personnel/api';
import type {PersonnelRecord} from '@/entities/personnel/types';
import {rosterQuery} from '@/features/manage-personnel/lib/roster';
import {runBulk, type BulkOutcome} from '@/shared/api/bulk';
import type {PersonnelImportResult} from '@/entities/personnel/api';
import {ApiError} from '@/shared/api';
import {CAN_DOWNLOAD, saveResponseAsFile} from '@/shared/lib/download';
import {pickSpreadsheet} from '@/shared/lib/file-picker';
import {isoDate} from '@/entities/attendance/period';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {DateField} from '@/shared/ui/date-field';
import {AppInput} from '@/shared/ui/input';
import {ChoiceChips, Segmented} from '@/shared/ui/segmented';
import {SectionHeader} from '@/shared/ui/section';
import {Sheet} from '@/shared/ui/sheet';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Everything that acts on MANY people at once: the roster import/export, and the three
 * batch operations the gateway offers.
 *
 * It is a sheet rather than a mode on the personnel list because multi-select and
 * "open the card" are different intentions, and a list that does both needs a mode
 * switch anyway — this IS that mode, with room to explain what each action does before
 * it is applied to forty people.
 *
 * ## Partial success is the normal case
 *
 * Every `/bulk/*` route answers **200 with a per-item outcome**, not all-or-nothing:
 * `succeeded` and `failed` both come back populated, and a screen that reads the
 * resolved promise as "done" is lying whenever `failed` is non-empty. So the result is
 * reported as a count of each, with the first few reasons named.
 *
 * The 500-id cap is handled by `runBulk` (shared/api/bulk), which chunks and merges —
 * a company with 600 people would otherwise get a 400 and no explanation.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  block: {gap: spacing(2)},
  card: {gap: spacing(3)},
  row: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  grow: {flexGrow: 1, flexBasis: 150},
  list: {maxHeight: 260},
  person: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingVertical: spacing(2),
    paddingHorizontal: spacing(2),
    borderRadius: radii.sm,
  },
  personText: {flex: 1, gap: 1},
  box: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tick: {width: 10, height: 10, borderRadius: 2},
});

type BulkAction = 'department' | 'accessUntil' | 'delete';

export function PersonnelToolsSheet({
  companyId,
  visible,
  onClose,
}: {
  companyId: string;
  visible: boolean;
  onClose: () => void;
}) {
  const {t, i18n} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState<BulkAction>('department');
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [accessUntil, setAccessUntil] = useState(() => isoDate(new Date()));
  const [unlimited, setUnlimited] = useState(false);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);
  const [imported, setImported] = useState<PersonnelImportResult | null>(null);

  const roster = useQuery({
    ...rosterQuery(companyId),
    enabled: visible,
  });

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId),
    enabled: visible,
  });

  const filtered = useMemo(() => {
    // Read through `roster.data` INSIDE the memo rather than off a `?? []` fallback
    // computed in the render body: that fallback is a new array identity every render
    // and would defeat the memo entirely.
    const source: PersonnelRecord[] = roster.data?.data ?? [];
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) {
      return source;
    }
    return source.filter(person =>
      [person.full_name, person.position, person.employee_number]
        .filter(Boolean)
        .some(field => field.toLocaleLowerCase().includes(needle)),
    );
  }, [roster.data, search]);

  const toggle = (recordId: string) =>
    setSelected(current =>
      current.includes(recordId)
        ? current.filter(id => id !== recordId)
        : [...current, recordId],
    );

  const apply = useMutation({
    mutationFn: async (): Promise<BulkOutcome> => {
      switch (action) {
        case 'department':
          return runBulk(selected, batch =>
            bulkSetPersonnelDepartments(companyId, batch, departmentIds),
          );
        case 'accessUntil':
          return runBulk(selected, batch =>
            bulkSetAccessUntil(
              companyId,
              batch,
              // 0 is the wire's "unlimited" — the server refuses to default this
              // field precisely so a missing value cannot mean it by accident.
              unlimited ? 0 : new Date(`${accessUntil}T23:59:59`).getTime(),
            ),
          );
        case 'delete':
          return runBulk(selected, batch => bulkDetachPersonnel(companyId, batch));
      }
    },
    onSuccess: async result => {
      setOutcome(result);
      setSelected([]);
      await qc.invalidateQueries({queryKey: ['personnel-list', companyId]});
    },
  });

  const download = useMutation({
    mutationFn: async (template: boolean) => {
      // Язык кабинета уезжает вместе с просьбой: заголовки и лист с правилами
      // читает тот же человек, который сейчас смотрит на этот экран.
      const res = await exportPersonnel(companyId, {
        format: 'xlsx',
        template,
        lang: i18n.language,
      });
      await saveResponseAsFile(
        res,
        `${t(template ? 'bulk.templateFile' : 'bulk.rosterFile')}.xlsx`,
      );
    },
  });

  const upload = useMutation({
    mutationFn: async (): Promise<PersonnelImportResult | null> => {
      const file: ImportFile | null = await pickSpreadsheet();
      if (!file) {
        return null;
      }
      return importPersonnel(companyId, file, i18n.language);
    },
    // Итог импорта — не итог пакетной операции, и складывать их в одно
    // состояние нельзя: у строк файла нет идентификаторов, сервер называет
    // их номером строки. Прежде ответ читали как `BulkOutcome`, то есть
    // спрашивали `succeeded.length` у поля, которого в ответе нет.
    onSuccess: async result => {
      if (result) {
        setImported(result);
        await qc.invalidateQueries({queryKey: ['personnel-list', companyId]});
      }
    },
  });

  /// Причины отказа приходят с сервера машинными строками. Показываем их
  /// человеку на его языке, а незнакомую — дословно: выдумывать перевод для
  /// сообщения, которого мы не знаем, значит врать.
  const issueText = (message: string): string => {
    if (message === 'fullname is required') {
      return t('bulk.importNoName');
    }
    if (message === 'department is required') {
      return t('bulk.importNoDepartment');
    }
    const unknown = /^unknown department '(.+)'$/.exec(message);
    if (unknown) {
      return t('bulk.importUnknownDepartment', {name: unknown[1]});
    }
    return message;
  };

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  const canApply =
    selected.length > 0 &&
    (action !== 'department' || departments.data !== undefined) &&
    !apply.isPending;

  return (
    <Sheet
      visible={visible}
      onClose={() => {
        setOutcome(null);
        apply.reset();
        onClose();
      }}
      title={t('bulk.title')}
      subtitle={t('bulk.subtitle')}
      testID="personnel-tools">
      <View style={styles.root}>
        {CAN_DOWNLOAD ? (
          <View style={styles.block}>
            <SectionHeader title={t('bulk.roster')} hint={t('bulk.rosterHintFull')} />
            <View style={styles.row}>
              <AppButton
                title={t('bulk.exportRoster')}
                tone="soft"
                size="sm"
                loading={download.isPending && download.variables === false}
                onPress={() => download.mutate(false)}
                style={styles.grow}
                testID="bulk-export"
              />
              <AppButton
                title={t('bulk.downloadTemplate')}
                tone="neutral"
                size="sm"
                loading={download.isPending && download.variables === true}
                onPress={() => download.mutate(true)}
                style={styles.grow}
                testID="bulk-template"
              />
              <AppButton
                title={t('bulk.import')}
                tone="neutral"
                size="sm"
                loading={upload.isPending}
                onPress={() => upload.mutate()}
                style={styles.grow}
                testID="bulk-import"
              />
            </View>
            {download.isError ? (
              <Banner tone="error" title={errorOf(download.error)} />
            ) : null}
            {upload.isError ? (
              <Banner tone="error" title={errorOf(upload.error)} />
            ) : null}
            {imported ? (
              <Banner
                tone={imported.issues.length > 0 ? 'warning' : 'success'}
                title={t('bulk.importDone', {
                  imported: imported.imported_count,
                  skipped: imported.skipped_count,
                })}
                message={
                  imported.issues.length > 0
                    ? [
                        t('bulk.importIssues', {count: imported.issues.length}),
                        // Пять строк — столько человек читает, не пролистывая.
                        // Остальные считаются, а не прячутся молча.
                        ...imported.issues
                          .slice(0, 5)
                          .map(i =>
                            t('bulk.importRow', {
                              row: i.row_number,
                              reason: issueText(i.message),
                            }),
                          ),
                        ...(imported.issues.length > 5
                          ? [t('bulk.importMore', {count: imported.issues.length - 5})]
                          : []),
                      ].join('\n')
                    : undefined
                }
              />
            ) : null}
          </View>
        ) : null}

        <View style={styles.block}>
          <SectionHeader
            title={t('bulk.select')}
            hint={t('bulk.selected', {count: selected.length})}
          />
          <AppInput
            label=""
            icon="search"
            value={search}
            onChangeText={setSearch}
            placeholder={t('reports.searchEmployees')}
            autoCapitalize="none"
            testID="bulk-search"
          />
          {roster.isPending ? (
            <Skeleton height={180} radius={radii.lg} />
          ) : (
            <Card padding={2}>
              <ScrollView style={styles.list} nestedScrollEnabled>
                {filtered.map(person => {
                  const on = selected.includes(person.record_id);
                  return (
                    <Pressable
                      key={person.record_id}
                      accessibilityRole="checkbox"
                      accessibilityState={{checked: on}}
                      onPress={() => toggle(person.record_id)}
                      style={({pressed}) => [
                        styles.person,
                        {backgroundColor: on ? p.accent : pressed ? p.muted : 'transparent'},
                      ]}
                      testID={`bulk-person-${person.record_id}`}>
                      <View
                        style={[
                          styles.box,
                          {
                            borderColor: on ? p.primary : p.input,
                            backgroundColor: on ? p.primary : 'transparent',
                          },
                        ]}>
                        {on ? (
                          <View
                            style={[styles.tick, {backgroundColor: p.primaryForeground}]}
                          />
                        ) : null}
                      </View>
                      <View style={styles.personText}>
                        <AppText variant="body" numberOfLines={1}>
                          {person.full_name}
                        </AppText>
                        <AppText variant="micro" numberOfLines={1}>
                          {[person.position, person.department_names.join(', ')]
                            .filter(Boolean)
                            .join(' · ')}
                        </AppText>
                      </View>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </Card>
          )}
          <View style={styles.row}>
            <AppButton
              title={t('bulk.selectAll')}
              tone="ghost"
              size="sm"
              onPress={() => setSelected(filtered.map(person => person.record_id))}
              style={styles.grow}
            />
            <AppButton
              title={t('bulk.clearSelection')}
              tone="ghost"
              size="sm"
              onPress={() => setSelected([])}
              style={styles.grow}
            />
          </View>
        </View>

        <View style={styles.block}>
          <SectionHeader title={t('bulk.action')} />
          <Card padding={4} style={styles.card}>
            <Segmented
              value={action}
              onChange={setAction}
              testID="bulk-action"
              options={[
                {value: 'department', label: t('bulk.actionDepartment')},
                {value: 'accessUntil', label: t('bulk.actionAccess')},
                {value: 'delete', label: t('bulk.actionDelete')},
              ]}
            />

            {action === 'department' ? (
              <>
                <AppText variant="caption">{t('bulk.departmentHint')}</AppText>
                <ChoiceChips
                  // Multi-select is expressed by toggling within a single-select
                  // control: the chips carry their own selected state from `selected`
                  // below, and picking one flips it rather than replacing the set.
                  value=""
                  onChange={id =>
                    setDepartmentIds(current =>
                      current.includes(id)
                        ? current.filter(v => v !== id)
                        : [...current, id],
                    )
                  }
                  options={(departments.data?.departments ?? []).map(d => ({
                    value: d.department_id,
                    label: departmentIds.includes(d.department_id)
                      ? `✓ ${d.name}`
                      : d.name,
                  }))}
                  testID="bulk-departments"
                />
              </>
            ) : null}

            {action === 'accessUntil' ? (
              <>
                <AppText variant="caption">{t('bulk.accessHint')}</AppText>
                <Segmented
                  value={unlimited ? 'unlimited' : 'until'}
                  onChange={value => setUnlimited(value === 'unlimited')}
                  options={[
                    {value: 'until', label: t('bulk.accessUntil')},
                    {value: 'unlimited', label: t('bulk.accessUnlimited')},
                  ]}
                />
                {!unlimited ? (
                  <DateField
                    label={t('bulk.accessUntil')}
                    value={accessUntil}
                    onChange={setAccessUntil}
                    testID="bulk-access-date"
                  />
                ) : null}
              </>
            ) : null}

            {action === 'delete' ? (
              <Banner
                tone="warning"
                title={t('bulk.deleteWarning')}
                message={t('bulk.deleteScope')}
              />
            ) : null}

            {apply.isError ? (
              <Banner tone="error" title={errorOf(apply.error)} />
            ) : null}

            {outcome ? (
              <Banner
                tone={outcome.failed.length > 0 ? 'warning' : 'success'}
                title={t('bulk.outcome', {
                  ok: outcome.succeeded.length,
                  failed: outcome.failed.length,
                })}
                message={
                  outcome.failed.length > 0
                    ? outcome.failed
                        .slice(0, 3)
                        .map(f => `${f.id}: ${f.error}`)
                        .join('\n')
                    : undefined
                }
              />
            ) : null}

            <AppButton
              title={t('bulk.apply', {count: selected.length})}
              tone={action === 'delete' ? 'destructive' : 'signal'}
              disabled={!canApply}
              loading={apply.isPending}
              onPress={() => {
                setOutcome(null);
                apply.mutate();
              }}
              testID="bulk-apply"
            />
          </Card>
        </View>
      </View>
    </Sheet>
  );
}
